import type { ReactElement } from 'react';

/**
 * The app's icon set — inline SVG, no dependency, no icon font, no emoji.
 *
 * Paths are lifted from `Infras App Redesign.dc.html`, which already carries them
 * at this exact contract, rather than redrawn: 16×16 viewBox, `fill="none"`,
 * stroke-only, `stroke="currentColor"`, 1.4 width, round caps and joins.
 *
 * `currentColor` is the whole point. An icon inherits the colour of the row it
 * sits in, so active / muted / disabled states need no icon-specific CSS — which
 * is what made the previous two-letter mono labels and emoji awkward: emoji carry
 * their own palette and ignore the theme entirely.
 *
 * Always `aria-hidden`. An icon never carries meaning on its own; the label beside
 * it does, and in the collapsed rail that label moves to `title` + `aria-label` on
 * the link itself.
 *
 * os-internal-ui was checked first, as phase 5 asks: it ships a favicon and theme
 * tokens, no icon set.
 */
export type IconName =
  | 'grid'
  | 'repo'
  | 'cluster'
  | 'key'
  | 'clock'
  | 'access'
  | 'catalog'
  | 'docs'
  | 'users'
  | 'shield'
  | 'question'
  | 'account'
  | 'search'
  | 'sun'
  | 'moon'
  | 'monitor'
  | 'chevron'
  | 'copy'
  | 'external'
  | 'run'
  | 'check'
  | 'alert'
  | 'quote'
  | 'close'
  | 'more'
  | 'cross'
  | 'settings'
  | 'inbox'
  | 'edit'
  | 'refresh'
  | 'trash';

/** Inner markup per icon. Stroke attributes that differ from the group default
 *  are set per-path; everything else inherits from the <svg>. */
const PATHS: Record<IconName, ReactElement> = {
  grid: (
    <>
      <rect x="1.75" y="1.75" width="5.5" height="5.5" rx="1.4" />
      <rect x="8.75" y="1.75" width="5.5" height="5.5" rx="1.4" />
      <rect x="1.75" y="8.75" width="5.5" height="5.5" rx="1.4" />
      <rect x="8.75" y="8.75" width="5.5" height="5.5" rx="1.4" />
    </>
  ),
  repo: <path d="M8 1.6l5.6 3v4.8L8 12.4 2.4 9.4V4.6L8 1.6z" strokeLinejoin="round" />,
  // Nodes joined into a cluster — deliberately not another cube, or repo and
  // cluster would read as the same thing in the rail.
  cluster: (
    <>
      <circle cx="8" cy="3.4" r="2.1" />
      <circle cx="3.2" cy="12" r="2.1" />
      <circle cx="12.8" cy="12" r="2.1" />
      <path d="M6.9 5.3L4.3 10.1M9.1 5.3l2.6 4.8" strokeWidth="1.3" />
    </>
  ),
  key: (
    <>
      <circle cx="5" cy="8" r="3.1" />
      <path d="M8.1 8H14M11.6 8v2.4M13.4 8v1.7" />
    </>
  ),
  clock: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 4.6V8l2.4 1.6" />
    </>
  ),
  access: (
    <>
      <rect x="3.2" y="7" width="9.6" height="6.6" rx="1.6" />
      <path d="M5.6 7V5.1a2.4 2.4 0 014.8 0V7" />
    </>
  ),
  catalog: (
    <>
      <rect x="2" y="2.4" width="12" height="3" rx="1" />
      <rect x="2" y="6.9" width="12" height="3" rx="1" />
      <rect x="2" y="11.4" width="12" height="2.6" rx="1" />
    </>
  ),
  docs: (
    <>
      <path d="M3.2 2.6h6.2l3.4 3.4v7.4H3.2V2.6z" strokeLinejoin="round" />
      <path d="M9.3 2.7V6h3.3M5.6 9h4.8M5.6 11.2h3.2" strokeWidth="1.2" />
    </>
  ),
  users: (
    <>
      <circle cx="6.1" cy="5.4" r="2.5" />
      <path d="M1.6 13.2c.4-2.4 2.3-3.7 4.5-3.7s4.1 1.3 4.5 3.7" />
      <path d="M11.1 3.3a2.3 2.3 0 010 4.3M12.4 9.9c1.2.5 2 1.7 2.2 3.3" />
    </>
  ),
  shield: (
    <>
      <path
        d="M8 1.9l5.1 2v4.3c0 3-2.1 5.1-5.1 5.9-3-.8-5.1-2.9-5.1-5.9V3.9l5.1-2z"
        strokeLinejoin="round"
      />
      <path d="M5.9 7.9l1.5 1.5 2.9-2.9" strokeLinejoin="round" />
    </>
  ),
  question: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M6.3 6.2a1.8 1.8 0 013.5.6c0 1.2-1.8 1.5-1.8 2.6M8 11.8v.1" />
    </>
  ),
  account: (
    <>
      <circle cx="8" cy="5.2" r="2.6" />
      <path d="M2.9 13.4c.5-2.6 2.6-4 5.1-4s4.6 1.4 5.1 4" />
    </>
  ),
  search: (
    <>
      <circle cx="7" cy="7" r="4.6" />
      <path d="M10.6 10.6L14 14" />
    </>
  ),
  sun: (
    <>
      <circle cx="8" cy="8" r="2.9" />
      <path
        d="M8 1.4v1.7M8 12.9v1.7M14.6 8h-1.7M3.1 8H1.4M12.7 3.3l-1.2 1.2M4.5 11.5l-1.2 1.2M12.7 12.7l-1.2-1.2M4.5 4.5L3.3 3.3"
        strokeWidth="1.3"
      />
    </>
  ),
  moon: <path d="M13.4 10.2A5.9 5.9 0 015.8 2.6a5.9 5.9 0 107.6 7.6z" strokeLinejoin="round" />,
  monitor: (
    <>
      <rect x="1.7" y="2.6" width="12.6" height="8.6" rx="1.5" />
      <path d="M5.6 13.9h4.8" />
    </>
  ),
  chevron: <path d="M6 3.2L10.8 8 6 12.8" strokeWidth="1.5" strokeLinejoin="round" />,
  // No copy or play glyph existed in the prototype; both are drawn to the same
  // contract rather than borrowed from another set.
  copy: (
    <>
      <rect x="5.6" y="5.6" width="7.8" height="7.8" rx="1.5" />
      <path d="M10.4 5.6V4.1a1.5 1.5 0 00-1.5-1.5H4.1a1.5 1.5 0 00-1.5 1.5v4.8a1.5 1.5 0 001.5 1.5h1.5" />
    </>
  ),
  external: (
    <>
      <path d="M6.6 9.4a2.6 2.6 0 003.9.3l2-2a2.6 2.6 0 00-3.7-3.7l-1.1 1.1" />
      <path d="M9.4 6.6a2.6 2.6 0 00-3.9-.3l-2 2a2.6 2.6 0 003.7 3.7l1.1-1.1" />
    </>
  ),
  run: <path d="M5.4 3.4l7 4.6-7 4.6V3.4z" strokeLinejoin="round" />,
  check: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M5.4 8.2l1.8 1.8 3.4-3.6" strokeWidth="1.5" strokeLinejoin="round" />
    </>
  ),
  alert: (
    // A bell, not a circled "!" — the old glyph read as an info icon at 16px.
    // Hand-drawn to the house contract (Feather proportions, rescaled).
    <>
      <path d="M12.7 6.3a4.7 4.7 0 0 0-9.4 0c0 5.2-2 6.6-2 6.6h13.4s-2-1.4-2-6.6" strokeLinejoin="round" />
      <path d="M9.1 13.4a1.3 1.3 0 0 1-2.2 0" />
    </>
  ),
  quote: (
    <path d="M6.4 4.4C4.6 5.2 3.6 6.7 3.6 8.6c0 1.5.9 2.6 2.2 2.6 1.1 0 1.9-.8 1.9-1.8 0-1-.7-1.7-1.7-1.7h-.3c.1-1 .8-1.8 1.8-2.3zM12.6 4.4c-1.8.8-2.8 2.3-2.8 4.2 0 1.5.9 2.6 2.2 2.6 1.1 0 1.9-.8 1.9-1.8 0-1-.7-1.7-1.7-1.7h-.3c.1-1 .8-1.8 1.8-2.3z" strokeLinejoin="round" />
  ),
  close: <path d="M4.2 4.2l7.6 7.6M11.8 4.2l-7.6 7.6" strokeWidth="1.5" />,
  // Overflow menu. VERTICAL on purpose — a horizontal ellipsis is the truncation
  // mark ("read more"), and readers apply that meaning whatever we intend. Drawn
  // as three zero-length round-capped strokes, so it stays on the set's
  // stroke-only contract instead of needing filled circles.
  more: (
    <path
      d="M8 3.9v.01M8 8v.01M8 12.1v.01"
      strokeWidth="2"
      strokeLinecap="round"
    />
  ),
  cross: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M5.9 5.9l4.2 4.2M10.1 5.9l-4.2 4.2" strokeWidth="1.5" />
    </>
  ),
  settings: (
    <>
      <path d="M2.4 4.6h11.2M2.4 11.4h11.2" />
      <circle cx="6" cy="4.6" r="1.7" />
      <circle cx="10.4" cy="11.4" r="1.7" />
    </>
  ),
  inbox: (
    <>
      <path d="M1.9 8.4h3.4l1 2h3.4l1-2h3.4" strokeLinejoin="round" />
      <path d="M2.9 3.2h10.2l1.4 5.2v4.6a1 1 0 01-1 1H2.5a1 1 0 01-1-1V8.4l1.4-5.2z" strokeLinejoin="round" />
    </>
  ),
  edit: (
    <>
      {/* pencil: rounded eraser end, ferrule line, point at bottom-left */}
      <path d="M11.1 2.6a1.75 1.75 0 012.5 2.5l-7.6 7.6-3.3.8.8-3.3z" strokeLinejoin="round" />
      <path d="M10.1 3.6l2.5 2.5" />
    </>
  ),
  /* Two arcs and their corner ticks — the icon-only Refresh control (pass 3).
     Drawn as two 135° arcs rather than one broken circle so the gap reads as a
     rotation at 16px, where a single arc with two heads reads as a comma. */
  refresh: (
    <>
      <path d="M13.4 8a5.4 5.4 0 01-9.2 3.8" />
      <path d="M2.6 8a5.4 5.4 0 019.2-3.8" />
      <path d="M11.8 1.6v2.8H9" strokeLinejoin="round" />
      <path d="M4.2 14.4v-2.8H7" strokeLinejoin="round" />
    </>
  ),
  trash: (
    <>
      <path d="M2.8 4.4h10.4" />
      <path d="M6.2 4.4V3.2a1 1 0 011-1h1.6a1 1 0 011 1v1.2" strokeLinejoin="round" />
      <path d="M4.1 4.4l.6 8.3a1.2 1.2 0 001.2 1.1h4.2a1.2 1.2 0 001.2-1.1l.6-8.3" strokeLinejoin="round" />
      <path d="M6.8 6.9v4.2M9.2 6.9v4.2" />
    </>
  ),
};

export function Icon({
  name,
  size = 16,
  className,
}: {
  name: IconName;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
